// ApiHelpers.ts
import { ResponseError } from "@app/Api/gen";

export class ProblemDetailsError extends Error {
	public readonly details: ProblemDetails;
	public readonly status: number;

	constructor(problemDetails: ProblemDetails) {
		super(problemDetails.title || `HTTP Error ${problemDetails.status || "Unknown"}`);
		this.name = "ProblemDetailsError";
		this.details = problemDetails;
		this.status = problemDetails.status || 500;
		Object.setPrototypeOf(this, ProblemDetailsError.prototype);
	}
}

export interface ProblemDetails {
	type?: string;
	title?: string;
	status?: number;
	detail?: string;
	instance?: string;
	[key: string]: unknown;
}

/**
 * Asynchronously converts a Response or ResponseError to a ProblemDetails object.
 * It attempts to parse the response body as JSON according to RFC 7807.
 * If parsing fails or the body is not in the expected format, it synthesizes
 * a ProblemDetails object using information from the Response object.
 *
 * @param responseOrError The Response object or a ResponseError.
 * @returns A Promise that resolves to a ProblemDetails object, or null if the input
 *          is neither a Response nor a ResponseError.
 */
export async function toProblemDetails(
	responseOrError: Response | ResponseError | unknown // Allow unknown for broader type guard
): Promise<ProblemDetails | null> {
	let response: Response;
	let baseErrorMessage: string | undefined; // To store message from ResponseError

	if (responseOrError instanceof Response) {
		response = responseOrError;
	} else if (responseOrError instanceof ResponseError) {
		response = responseOrError.response;
		baseErrorMessage = responseOrError.message; // Capture the original error message
	} else {
		// Not a type we can process
		// console.warn('Input to toProblemDetails is not a Response or ResponseError.');
		return null;
	}

	let problemDetails: ProblemDetails;

	try {
		// Use .clone() as the response body can only be consumed once
		const body = await response.clone().json();

		if (typeof body === "object" && body !== null) {
			problemDetails = {
				type: body.type,
				title: body.title || response.statusText || "Error",
				status: body.status || response.status,
				detail: body.detail,
				instance: body.instance,
				...body
			};
		} else {
			problemDetails = {
				type: "about:blank",
				title: `HTTP Error ${response.status}`,
				status: response.status,
				detail:
					`Response was JSON but not in the expected ProblemDetails object structure. Received: ${JSON.stringify(body)}.` +
					(baseErrorMessage ? ` Original error: ${baseErrorMessage}` : "")
			};
		}
	} catch (e: unknown) {
		let responseText = "Response body could not be parsed as JSON or was empty.";
		try {
			// Clone again in case the first clone().json() failed mid-stream or if it's a different error
			const text = await response.clone().text();
			if (text) {
				// Limit the length of the text to avoid overly long error messages
				responseText = `Details: ${text.substring(0, 500)}${text.length > 500 ? "..." : ""}`;
			}
		} catch (textError) {
			// Silently ignore if reading as text also fails
		}
		problemDetails = {
			type: "about:blank",
			title: `HTTP Error ${response.status} ${response.statusText || ""}`.trim(),
			status: response.status,
			detail: `Request failed. ${responseText}` + (baseErrorMessage ? ` Original error: ${baseErrorMessage}` : "")
		};
	}

	return problemDetails;
}
